import React, { useCallback, useMemo, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CancelCrossIcon, Sheet, ArrowUpIcon, ArrowDownIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import queryString from "query-string";
import ErrorBoundary from "~/components/ErrorBoundry";
import { useGoogleDriveListener } from "~/components/Google/useGoogleDriveListener";
import SectionLoader from "~/components/SectionLoader/SectionLoader";
import { useObjectSheetContext } from "~/components/Sheet/ObjectSheetContext";
import { TaskTrashSlate } from "~/components/Task/TaskTrashSlate";
import { Task } from "~/components/tasks/Task";
import { getTaskById, getTaskHashUrl } from "~/store/selectors/selector.tasks";
import Angie from "~/utils/angie";
import { TaskFormContextProvider } from "../../tasks/context";

export const TaskSheet = React.forwardRef(({ taskId, projectId, onClose, open, prev, next, showControls }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const editSearchParam = queryString.parse(search)?.edit;
  const [showTaskForm, setShowTaskForm] = useState(() => editSearchParam);
  const handleShowEditTaskForm = useCallback(() => {
    setShowTaskForm(true);
  }, []);
  const task = useSelector((state) => getTaskById(state, taskId));

  const { closeSheet } = useObjectSheetContext();
  const googlePicker = useGoogleDriveListener();

  const handleCloseEditTaskForm = useCallback(() => {
    if (editSearchParam) {
      const urlWithoutEditParam = window.location.href.replace(/(\?|&)edit=true/, "");
      window.history.pushState(null, "", urlWithoutEditParam);
    }
    setShowTaskForm(false);
  }, [editSearchParam]);

  const prevTask = useSelector((state) => getTaskHashUrl(state, prev));
  const nextTask = useSelector((state) => getTaskHashUrl(state, next));

  const handlePrev = useCallback(() => {
    if (prev) {
      Angie.logger.info("Task navigation button clicked.", {
        direction: "prev",
        navigation_source: "sheet",
        navigation_type: "task",
      });
      navigate(`${pathname}?modal=${prevTask}`);
    }
  }, [navigate, pathname, prev, prevTask]);

  const handleNext = useCallback(() => {
    if (next) {
      Angie.logger.info("Task navigation button clicked.", {
        direction: "next",
        navigation_source: "sheet",
        navigation_type: "task",
      });
      navigate(`${pathname}?modal=${nextTask}`);
    }
  }, [navigate, next, nextTask, pathname]);

  const handleClose = useCallback(() => {
    if (typeof onClose === "function") onClose();
    if (showTaskForm) handleCloseEditTaskForm();
  }, [handleCloseEditTaskForm, onClose, showTaskForm]);

  const controls = useMemo(
    () =>
      task && task.project_id === projectId && Boolean(prev || next) && showControls && !showTaskForm
        ? [
            {
              tooltip: t("Close"),
              onClick: closeSheet,
              icon: <CancelCrossIcon />,
              className: "task_modal_close_icon tw-order-1 sm:tw-order-1",
            },
            {
              tooltip: prev ? t("Previous Task") : t("You are at beginning of the list"),
              onClick: handlePrev,
              icon: <ArrowUpIcon />,
              disabled: !prev,
              className: "tw-order-3 sm:tw-order-2",
            },
            {
              tooltip: next ? t("Next Task") : t("You have reached the end of the list"),
              onClick: handleNext,
              icon: <ArrowDownIcon />,
              disabled: !next,
              className: "tw-order-2 sm:tw-order-3",
            },
          ]
        : [
            {
              tooltip: t("Close"),
              onClick: closeSheet,
              icon: <CancelCrossIcon />,
              className: "task_modal_close_icon",
            },
          ],
    [closeSheet, handleNext, handlePrev, next, prev, projectId, showControls, showTaskForm, t, task]
  );

  return (
    <Sheet
      ref={ref}
      open={open}
      position="right"
      animation="right"
      mode="stretch"
      onClose={handleClose}
      controls={controls}
      className="task-modal-container"
      disableFocusLock={googlePicker}
    >
      <Suspense fallback={<SectionLoader />}>
        <ErrorBoundary errorLocation="Task Modal">
          <TaskFormContextProvider
            value={{
              showTaskForm,
              handleShowEditTaskForm,
              handleCloseEditTaskForm,
            }}
          >
            <TaskTrashSlate
              taskId={taskId}
              projectId={projectId}
            >
              <Task
                projectId={projectId}
                taskId={taskId}
                openInModal
              />
            </TaskTrashSlate>
          </TaskFormContextProvider>
        </ErrorBoundary>
      </Suspense>
    </Sheet>
  );
});

TaskSheet.propTypes = {
  taskId: PropTypes.number,
  projectId: PropTypes.number,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  showControls: PropTypes.bool,
  prev: PropTypes.number,
  next: PropTypes.number,
};

TaskSheet.defaultProps = {
  taskId: 0,
  projectId: 0,
  open: false,
  showControls: false,
  prev: null,
  next: null,
};

TaskSheet.displayName = "TaskSheet";
